import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import SiteFooter from '../../components/feature/SiteFooter';

export default function ContactPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const faqSchemaItems = [
    { q: 'How long does it take to get a response?', a: 'We typically respond within 1–2 business days for general inquiries, and within 3–5 days for review or partnership requests.' },
    { q: 'Can I request a specific tool to be reviewed?', a: 'Absolutely! Use our Submit a Tool page to request a review. We prioritize based on reader demand and relevance.' },
    { q: 'Do you accept guest posts or sponsored content?', a: 'We accept guest contributions from qualified experts but clearly label them. Sponsored content is accepted with full disclosure.' },
    { q: 'How can I report an inaccuracy in a review?', a: 'Email us at corrections@247digitrend.com with the tool name and the specific claim you want us to verify.' },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        name: 'Contact 247digitrend',
        url: `${siteUrl}/contact`,
        description:
          'Contact the 247digitrend editorial team for general inquiries, review requests, partnership proposals, or corrections.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqSchemaItems.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ],
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    fetch('https://readdy.ai/api/form/d6v42enb245v7h34oj1g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    }).then(() => {
      form.reset();
      setCharCount(0);
      setSubmitted(true);
    });
  };

  const faqs = [
    { q: 'How long does it take to get a response?', a: 'We typically respond within 1–2 business days for general inquiries, and within 3–5 days for review or partnership requests.' },
    { q: 'Can I request a specific tool to be reviewed?', a: 'Absolutely! Use our Submit a Tool page to request a review. We prioritize based on reader demand and relevance.' },
    { q: 'Do you accept guest posts or sponsored content?', a: 'We accept guest contributions from qualified experts but clearly label them. Sponsored content is accepted with full disclosure.' },
    { q: 'How can I report an inaccuracy in a review?', a: 'Email us at corrections@247digitrend.com with the tool name and the specific claim you want us to verify.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Contact Us – 247digitrend | Editorial, Partnerships & Review Requests"
        description="Get in touch with the 247digitrend team. We respond to general inquiries, review requests, partnership proposals and inaccuracy corrections within 1–2 business days."
        canonical={`${siteUrl}/contact`}
        keywords="contact 247digitrend, review request, partnership inquiry, editorial contact"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-44 pb-16" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-mail-line"></i> Get In Touch
          </span>
          <h1 className="text-5xl font-black text-gray-900 mb-4 leading-tight">We&apos;d love to hear from you</h1>
          <p className="text-lg text-gray-600 leading-relaxed">Whether you have a question, a correction, or a partnership idea — our team reads every message.</p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-5 gap-12">
            {/* FORM */}
            <div className="md:col-span-3">
              <h2 className="text-xl font-black text-gray-900 mb-6">Send Us a Message</h2>
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 bg-violet-50 rounded-2xl border border-violet-100">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-violet-100 mb-4">
                    <i className="ri-check-line text-violet-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-black text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-sm text-gray-500 text-center max-w-xs">Thanks for reaching out. We&apos;ll get back to you within 1–2 business days.</p>
                  <button onClick={() => setSubmitted(false)} className="mt-6 text-violet-600 text-sm font-semibold cursor-pointer hover:underline">Send another message</button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d6v42enb245v7h34oj1g"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                      <input name="name" type="text" required placeholder="John Smith" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                      <input name="email" type="email" required placeholder="john@example.com" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Subject <span className="text-red-400">*</span></label>
                    <select name="subject" required className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors bg-white cursor-pointer">
                      <option value="">Select a subject...</option>
                      <option value="General Question">General Question</option>
                      <option value="Review Request">Review Request</option>
                      <option value="Partnership Inquiry">Partnership Inquiry</option>
                      <option value="Correction / Inaccuracy">Correction / Inaccuracy</option>
                      <option value="Press Inquiry">Press Inquiry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Message <span className="text-red-400">*</span></label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell us what's on your mind..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors resize-none"
                    ></textarea>
                    <div className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>{charCount}/500</div>
                    {charCount >= 500 && <p className="text-xs text-red-500 mt-1">Message limit reached. Please shorten your message.</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={charCount >= 500}
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold py-3.5 rounded-xl whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* CONTACT INFO */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-6">Contact Information</h2>
              </div>
              {[
                { icon: 'ri-mail-line', color: 'from-violet-500 to-fuchsia-500', label: 'General Inquiries', value: 'hello@247digitrend.com' },
                { icon: 'ri-edit-line', color: 'from-emerald-500 to-teal-500', label: 'Editorial', value: 'editorial@247digitrend.com' },
                { icon: 'ri-megaphone-line', color: 'from-orange-400 to-pink-500', label: 'Advertising', value: 'ads@247digitrend.com' },
                { icon: 'ri-map-pin-line', color: 'from-sky-500 to-cyan-500', label: 'Office', value: 'Ho Chi Minh City, Vietnam' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-2xl border border-gray-100">
                  <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br ${item.color}`}>
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 mb-0.5">{item.label}</div>
                    <div className="text-sm font-semibold text-gray-800">{item.value}</div>
                  </div>
                </div>
              ))}
              <div className="p-4 rounded-2xl bg-violet-50 border border-violet-100">
                <div className="text-xs font-bold text-violet-700 mb-1">Response Time</div>
                <div className="text-sm text-gray-600">We respond to all messages within <strong>1–2 business days</strong>.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" style={{ background: '#faf5ff' }}>
        <div className="max-w-3xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-gray-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-sm text-gray-500">Quick answers before you write to us</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-violet-500 mt-0.5 flex-shrink-0"></i>
                  {faq.q}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed pl-5">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
